import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import AccountSubscriptionsCancelationModal from "./AccountSubscriptionsCancelationModal.jsx";
import AccountSubscriptionsInvoicesModal from "./AccountSubscriptionsInvoicesModal.jsx";
import API from "../config/api.js";

function SubscriptionRow({ sub, iduser, onCancel, onInvoices }) {
  const typeLabel = sub.type === "3 month" ? "Quarterly Subscription"
    : sub.type === "month" ? "Monthly Subscription"
    : "Annual Subscription";

  const isActive  = sub.status === "active" || sub.status === "trialing";
  const isCanceled = sub.status === "cancelled" || sub.status === "canceled";
  const canCancel = (sub.gateway === "stripe" || sub.gateway === "paypal")
    && !sub.canceledat && !isCanceled && sub.status !== "pending";

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }) : "";
  const fmtLong = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

  return (
    <div className="row align-items-center py-2 border-bottom">
      <div className="col-md-5 text-truncate">
        <span className="fw-bold text-primary me-3 text-uppercase">{typeLabel}</span>
        <span className="badge text-dark">{sub.status}</span>
      </div>
      <div className="col-md-3">{fmt(sub.createdat)} – {fmt(sub.currentperiodend)}</div>
      <div className="col-md-2">${Number(sub.price).toFixed(2)}/{sub.type}</div>
      <div className="col-md-2 text-end">
        <div className="dropdown">
          <button className="btn" type="button" data-bs-toggle="dropdown">
            <i className="fi fi-rr-menu-dots" />
          </button>
          <ul className="dropdown-menu">
            {canCancel && (
              <li>
                <button className="dropdown-item" onClick={() => onCancel({ idsubscription: sub.idsubscription, gateway: sub.gateway, idgateway: sub.idgateway })}>
                  Cancel
                </button>
              </li>
            )}
            {sub.gateway === "stripe" && (
              <>
                <li><a className="dropdown-item" href={`${API.stripe}?idgateway=${sub.idgateway}&manage&iduser=${iduser}`}>Payment method</a></li>
                <li><a className="dropdown-item" href={`${API.stripe}?idgateway=${sub.idgateway}&manage&iduser=${iduser}`}>Upgrade Plan</a></li>
                <li>
                  <button className="dropdown-item" onClick={() => onInvoices(sub.idgateway)}>
                    Invoices
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      {sub.status === "trialing" && !sub.canceledat && (
        <div className="col-12 mt-2">
          <div className="bg-light rounded-4 border p-3">
            <div className="row g-4 align-items-center">
              <div className="col-md-9">
                <p className="mb-0">Your subscription activates on {fmtLong(sub.currentperiodend)}. Unlock full features now.</p>
              </div>
              <div className="col-md-3">
                <button type="button" className="btn btn-primary w-100" onClick={() => window._openSkipTrialModal?.()}>
                  Become a member now!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isActive && sub.canceledat && sub.gateway === "stripe" && (
        <div className="col-12 mt-2">
          <div className="bg-light rounded-4 border p-3">
            <div className="row align-items-center">
              <div className="col-md-9">
                <p className="mb-0">Active until {fmtLong(sub.currentperiodend)}. Reactivate now and keep your weekly client pipeline moving.</p>
              </div>
              <div className="col-md-3">
                <button type="button" className="btn btn-primary w-100" onClick={() => window._openSkipTrialModal?.()}>
                  Reactivate Subscription
                </button>
              </div>

                
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccountSubscriptions() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [cancellation, setCancellation]   = useState(null); // { idsubscription, gateway, idgateway }
  const [invoicesGateway, setInvoicesGateway] = useState(null); // idgateway string
  const syncCancelRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API.account}?subscriptions`, { credentials: "include" });
      const data = await res.json();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sequential sync of stripe/paypal subscriptions, with 3s gap between requests.
  useEffect(() => {
    if (loading || subscriptions.length === 0) return;

    syncCancelRef.current = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      const targets = subscriptions.filter(
        (s) => s.gateway === "stripe" || s.gateway === "paypal"
      );

      for (let i = 0; i < targets.length; i++) {
        if (syncCancelRef.current) return;

        const sub  = targets[i];
        const base = sub.gateway === "stripe" ? API.stripe : API.paypal;
        const url  = `${base}?syncSubscription&idgateway=${encodeURIComponent(sub.idgateway)}`;

        try {
          const res  = await fetch(url, { credentials: "include" });
          const data = await res.json();
          const newStatus = data?.status;

          if (!syncCancelRef.current && newStatus && newStatus !== sub.status) {
            setSubscriptions((prev) =>
              prev.map((s) =>
                s.idsubscription === sub.idsubscription ? { ...s, status: newStatus } : s
              )
            );
          }
        } catch (_) {}

        if (i < targets.length - 1) await sleep(3000);
      }
    })();

    return () => { syncCancelRef.current = true; };
  }, [loading, subscriptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border rounded-4 p-4">
      <h2 className="fs-6 fw-bold">Subscriptions</h2>
      <p className="text-muted">Manage your billing and payment details.</p>

      <div className="p-2 px-3 rounded-3" style={{ background: "#F7F5FD" }}>
        {loading ? (
          <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
        ) : subscriptions.length === 0 ? (
          <>
            <div className="fw-bold mb-3">You don't have a subscription.</div>
            <a href="/plans" className="btn btn-primary">Subscribe now</a>
          </>
        ) : (
          subscriptions.map((sub) => <SubscriptionRow key={sub.idsubscription} sub={sub} iduser={user?.iduser || ''} onCancel={setCancellation} onInvoices={setInvoicesGateway} />)
        )}
      </div>

      {invoicesGateway && (
        <AccountSubscriptionsInvoicesModal
          idgateway={invoicesGateway}
          onClose={() => setInvoicesGateway(null)}
        />
      )}

      {cancellation && (
        <AccountSubscriptionsCancelationModal
          idsubscription={cancellation.idsubscription}
          gateway={cancellation.gateway}
          idgateway={cancellation.idgateway}
          onClose={() => setCancellation(null)}
        />
      )}
    </div>
  );
}